import React, { useEffect, useState } from "react";

import BugReportIcon from "@mui/icons-material/BugReport";
import BuildIcon from "@mui/icons-material/Build";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import PsychologyIcon from "@mui/icons-material/Psychology";
import WarningIcon from "@mui/icons-material/Warning";
import {
  Alert,
  Box,
  Button,
  Checkbox,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  Grid,
  IconButton,
  List,
  ListItem,
  ListItemText,
  Paper,
  TextField,
  Tooltip,
  Typography,
  useTheme,
} from "@mui/material";
import { keyframes, styled } from "@mui/material/styles";
import { useParams } from "react-router-dom";

import {
  AnalyseDiagnosticFileResponse,
  DiagnosticFile,
} from "@safetyculture/s12-apis-connect-web/s12/tools/support/v1/support_pb";

import { analyseDiagnosticFile } from "../../../api/endpoints/user";
import { callOptionsHeaders } from "api/helpers";
import { UserUrlParamTypes } from "types/user-display";

interface AnalysisDialogProps {
  open: boolean;
  file: DiagnosticFile;
  onClose: () => void;
}

type DialogState = "analysing" | "error" | "success" | "idle";

const rotate = keyframes`
  from {
    background-position: 0% 50%;
  }
  to {
    background-position: 200% 50%;
  }
`;

const DialogWrapper = styled(Box)<{ $state: DialogState }>(
  ({ theme, $state }) => ({
    position: "relative",
    borderRadius: theme.shape.borderRadius,
    padding: "4px",
    background: `linear-gradient(90deg, ${theme.palette.primary.main}, ${theme.palette.secondary.main}, ${theme.palette.primary.main})`,
    backgroundSize: $state === "analysing" ? "200% 100%" : "100% 100%",
    animation: $state === "analysing" ? `${rotate} 2s linear infinite` : "none",
  })
);

const fadeIn = keyframes`
  from {
    opacity: 0;
    transform: translateY(10px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
`;

const LoadingMessage = styled(Typography)(({ theme }) => ({
  animation: `${fadeIn} 0.5s ease-out`,
  marginBottom: theme.spacing(2),
  textAlign: "center",
  color: theme.palette.text.secondary,
}));

const AnalysisDialog: React.FC<AnalysisDialogProps> = ({
  open,
  file,
  onClose,
}) => {
  const { userId } = useParams<UserUrlParamTypes>();
  const [description, setDescription] = useState("");
  const [isAnalysing, setIsAnalysing] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [analysisResult, setAnalysisResult] =
    useState<AnalyseDiagnosticFileResponse | null>(null);
  const [copySuccess, setCopySuccess] = useState(false);
  const [loadingMessage, setLoadingMessage] = useState("");
  const [disableLogTrimming, setDisableLogTrimming] = useState(false);
  const theme = useTheme();

  const fileSizeInKB = Number(file.size) / 1024;
  const isSmallFile = fileSizeInKB < 100;

  useEffect(() => {
    const loadingMessages = [
      "Checking for diagnostic zip file...",
      "Pre-processing logs for analysis...",
      "Analysing logs, this can take a while...",
    ];

    let messageIndex = 0;
    let messageInterval: NodeJS.Timeout;

    if (isAnalysing) {
      setLoadingMessage(loadingMessages[0]);
      messageInterval = setInterval(() => {
        messageIndex++;
        if (messageIndex < loadingMessages.length) {
          setLoadingMessage(loadingMessages[messageIndex]);
        } else {
          clearInterval(messageInterval);
        }
      }, 5000);
    }

    return () => {
      if (messageInterval) {
        clearInterval(messageInterval);
      }
    };
  }, [isAnalysing]);

  const handleAnalyse = async () => {
    setIsAnalysing(true);
    setError(null);
    setAnalysisResult(null);
    try {
      const response = await analyseDiagnosticFile(
        {
          userId,
          filename: file.filename,
          problemDescription: description,
          disableLogTrimming,
        },
        callOptionsHeaders("us", userId) // all diagnostic logs are uploaded to US region
      );
      setAnalysisResult(response);
    } catch (error) {
      setError("Failed to analyse the file. Please try again.");
    } finally {
      setIsAnalysing(false);
    }
  };

  const handleClose = () => {
    setAnalysisResult(null);
    setDescription("");
    onClose();
  };

  const handleCopyResult = () => {
    if (!analysisResult) return;

    const resultText = `
Analysis Result:
${analysisResult.analysisResult}

${
  analysisResult.identifiedIssues.length > 0
    ? `Identified Issues:\n${analysisResult.identifiedIssues
        .map((issue) => `${issue}`)
        .join("\n")}`
    : ""
}

${
  analysisResult.troubleshootingSteps.length > 0
    ? `Troubleshooting Steps:\n${analysisResult.troubleshootingSteps
        .map((step) => `${step}`)
        .join("\n")}`
    : ""
}
    `.trim();

    navigator.clipboard.writeText(resultText).then(() => {
      setCopySuccess(true);
      setTimeout(() => setCopySuccess(false), 2000);
    });
  };

  const getDialogState = (): DialogState => {
    if (isAnalysing) return "analysing";
    if (error) return "error";
    if (analysisResult) return "success";
    return "idle";
  };

  const formatQuotedText = (text: string) => {
    return text.split("\n").map((line, lineIndex) => {
      // Count single quotes in the line
      const quoteCount = (line.match(/'/g) || []).length;

      // Only process if there are matching pairs of quotes
      if (quoteCount % 2 === 0 && quoteCount > 0) {
        const parts = line.split(/'([^']*)'/g);
        return (
          <React.Fragment key={lineIndex}>
            {parts.map((part, index) => {
              // Even indices are non-quoted text, odd indices are quoted text
              if (index % 2 === 1) {
                return <strong key={`${lineIndex}-${index}`}>{part}</strong>;
              }
              return part;
            })}
            {lineIndex < text.split("\n").length - 1 && <br />}
          </React.Fragment>
        );
      }

      // Return unformatted line if no matching quotes
      return (
        <React.Fragment key={lineIndex}>
          {line}
          {lineIndex < text.split("\n").length - 1 && <br />}
        </React.Fragment>
      );
    });
  };

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="md" fullWidth>
      <DialogWrapper $state={getDialogState()}>
        <DialogTitle
          style={{ backgroundColor: "#fff", borderRadius: "3px 3px 0 0" }}
        >
          <Grid container justifyContent="space-between" alignItems="center">
            <Grid item>Analyse with AI</Grid>
            {analysisResult && (
              <Grid item>
                <Tooltip title={copySuccess ? "Copied!" : "Copy Results"}>
                  <IconButton onClick={handleCopyResult} size="small">
                    <ContentCopyIcon />
                  </IconButton>
                </Tooltip>
              </Grid>
            )}
          </Grid>
        </DialogTitle>
        <DialogContent style={{ backgroundColor: "#fff" }}>
          <Box sx={{ mt: 2 }}>
            <Grid container spacing={2} alignItems="center">
              <Grid item xs={12}>
                <Typography variant="subtitle2" gutterBottom>
                  File: {file.filename}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Size: {(fileSizeInKB / 1024).toFixed(2)} MB
                </Typography>
              </Grid>
              {isSmallFile && (
                <Grid item xs={12}>
                  <Alert severity="info" sx={{ mb: 2 }}>
                    This file is small enough to analyse without log trimming.
                    You can disable log trimming to get a more detailed
                    analysis.
                  </Alert>
                </Grid>
              )}
              {!isSmallFile && disableLogTrimming && (
                <Grid item xs={12}>
                  <Alert severity="warning" sx={{ mb: 2 }}>
                    <Box sx={{ display: "flex", alignItems: "center" }}>
                      <WarningIcon sx={{ mr: 1 }} />
                      <Typography>
                        Warning: This file is large ({fileSizeInKB.toFixed(2)}{" "}
                        KB). Disabling log trimming may result in slower
                        analysis and higher resource usage.
                      </Typography>
                    </Box>
                  </Alert>
                </Grid>
              )}
            </Grid>
            {error && (
              <Alert severity="error" sx={{ mb: 2 }}>
                {error}
              </Alert>
            )}
            {!analysisResult && !isAnalysing && (
              <>
                <TextField
                  autoFocus
                  margin="dense"
                  label="Issue Description"
                  fullWidth
                  multiline
                  rows={4}
                  value={description}
                  onChange={(e) => setDescription(e.target.value)}
                  placeholder="Please describe the issue the user is experiencing, this is optional"
                  disabled={isAnalysing}
                  sx={{ mb: 2 }}
                />
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={disableLogTrimming}
                      onChange={(e) => setDisableLogTrimming(e.target.checked)}
                      disabled={isAnalysing}
                    />
                  }
                  label="Disable log trimming for more detailed analysis"
                />
              </>
            )}
            {isAnalysing && (
              <Box
                sx={{
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "center",
                  py: 4,
                }}
              >
                <CircularProgress size={40} sx={{ mb: 2 }} />
                <LoadingMessage variant="body1">
                  {loadingMessage}
                </LoadingMessage>
              </Box>
            )}
            {analysisResult && (
              <Grid container spacing={2}>
                <Grid item xs={12}>
                  <Alert
                    severity="info"
                    sx={{
                      mb: 2,
                      "& .MuiAlert-icon": {
                        alignItems: "center",
                      },
                    }}
                  >
                    <Typography variant="body2">
                      <strong>AI-Generated Analysis</strong>
                    </Typography>
                    <Typography variant="body2">
                      This analysis is generated by AI and should be used as a
                      reference. Manual verification is required. Not all
                      detected issues may be relevant to the reported problem -
                      please focus on issues that directly relate to the
                      user&apos;s reported issue.
                    </Typography>
                  </Alert>
                </Grid>
                <Grid item xs={12}>
                  <Paper
                    elevation={0}
                    sx={{ p: 2, bgcolor: theme.palette.background.default }}
                  >
                    <Typography variant="h6" gutterBottom>
                      Analysis Result
                    </Typography>
                    <Box
                      sx={{
                        maxHeight: "200px",
                        overflowY: "auto",
                        "&::-webkit-scrollbar": {
                          width: "8px",
                        },
                        "&::-webkit-scrollbar-track": {
                          background: theme.palette.grey[100],
                          borderRadius: "4px",
                        },
                        "&::-webkit-scrollbar-thumb": {
                          background: theme.palette.grey[400],
                          borderRadius: "4px",
                          "&:hover": {
                            background: theme.palette.grey[500],
                          },
                        },
                      }}
                    >
                      <Typography
                        variant="body1"
                        sx={{ whiteSpace: "pre-wrap" }}
                      >
                        {formatQuotedText(analysisResult.analysisResult)}
                      </Typography>
                    </Box>
                  </Paper>
                </Grid>

                {analysisResult.identifiedIssues.length > 0 && (
                  <Grid item xs={12} md={6}>
                    <Paper
                      elevation={0}
                      sx={{
                        p: 2,
                        bgcolor: theme.palette.error.light,
                        color: theme.palette.error.contrastText,
                      }}
                    >
                      <Box
                        sx={{ display: "flex", alignItems: "center", mb: 1 }}
                      >
                        <BugReportIcon sx={{ mr: 1 }} />
                        <Typography variant="h6">Identified Issues</Typography>
                      </Box>
                      <List dense>
                        {analysisResult.identifiedIssues.map((issue, index) => (
                          <ListItem key={index}>
                            <ListItemText primary={formatQuotedText(issue)} />
                          </ListItem>
                        ))}
                      </List>
                    </Paper>
                  </Grid>
                )}

                {analysisResult.troubleshootingSteps.length > 0 && (
                  <Grid item xs={12} md={6}>
                    <Paper
                      elevation={0}
                      sx={{
                        p: 2,
                        bgcolor: theme.palette.info.light,
                        color: theme.palette.info.contrastText,
                      }}
                    >
                      <Box
                        sx={{ display: "flex", alignItems: "center", mb: 1 }}
                      >
                        <BuildIcon sx={{ mr: 1 }} />
                        <Typography variant="h6">
                          Troubleshooting Steps
                        </Typography>
                      </Box>
                      <List dense>
                        {analysisResult.troubleshootingSteps.map(
                          (step, index) => (
                            <ListItem key={index}>
                              <ListItemText primary={formatQuotedText(step)} />
                            </ListItem>
                          )
                        )}
                      </List>
                    </Paper>
                  </Grid>
                )}
              </Grid>
            )}
          </Box>
        </DialogContent>
        <DialogActions
          style={{ backgroundColor: "#fff", borderRadius: "0 0 3px 3px" }}
        >
          <Button onClick={handleClose}>
            {analysisResult ? "Close" : "Cancel"}
          </Button>
          {!analysisResult && !isAnalysing && (
            <Button
              onClick={handleAnalyse}
              variant="contained"
              color="primary"
              disabled={isAnalysing}
              startIcon={<PsychologyIcon />}
            >
              Analyse
            </Button>
          )}
          {analysisResult && (
            <Button
              onClick={() => {
                setAnalysisResult(null);
                setDescription("");
              }}
              variant="outlined"
              color="primary"
              startIcon={<PsychologyIcon />}
            >
              Re-analyse
            </Button>
          )}
        </DialogActions>
      </DialogWrapper>
    </Dialog>
  );
};

export default AnalysisDialog;
